import { collection, doc, deleteDoc, setDoc, Timestamp, updateDoc } from 'firebase/firestore';

import { logAnalyticsEvent } from '../analytics/analytics';
import { useAuth } from '../contextProviders/AuthProvider';
import { useData } from '../contextProviders/DataProvider';
import { firestore } from '../firebase';
import League, { LEAGUE_STATUSES } from '../types/League';
import Org from '../types/Org';

export const useLeagueMutators = () => {
  const { isGod } = useAuth();
  const { isLeagueAdmin, isOrgAdmin, userState } = useData();

  const addLeague = async (league: League, org?: Org): Promise<League | null> => {
    if (userState.status !== 'ready' || !userState.userProfile) {
      console.error('User profile not found');
      return null;
    }

    if (!league.orgId) {
      console.error('League orgId not found');
      return null;
    }

    if (!isOrgAdmin(league.orgId)) {
      console.error('Permission denied: must be admin of organization');
      return null;
    }

    try {
      // Use provided itemId if available, otherwise generate a new one
      const leagueRef = league.itemId
        ? doc(firestore, `org/${league.orgId}/league/${league.itemId}`)
        : doc(collection(firestore, `org/${league.orgId}/league`));

      // Copy event defaults from org if provided and league doesn't have them set
      const newLeague: League = {
        ...league,
        creatorId: userState.userProfile?.itemId,
        createTime: Timestamp.now(),
        itemId: leagueRef.id,
        // Inherit org defaults if not explicitly set on league
        ...((league.defaultEventSignupOpensDaysPrior ?? org?.defaultEventSignupOpensDaysPrior) !== undefined && { defaultEventSignupOpensDaysPrior: league.defaultEventSignupOpensDaysPrior ?? org?.defaultEventSignupOpensDaysPrior }),
        ...((league.defaultEventSignupClosesDaysPrior ?? org?.defaultEventSignupClosesDaysPrior) !== undefined && { defaultEventSignupClosesDaysPrior: league.defaultEventSignupClosesDaysPrior ?? org?.defaultEventSignupClosesDaysPrior }),
        ...((league.defaultCoursePair ?? org?.defaultCoursePair) !== undefined && { defaultCoursePair: league.defaultCoursePair ?? org?.defaultCoursePair }),
        ...((league.defaultGhinIndexSetDaysPrior ?? org?.defaultGhinIndexSetDaysPrior) !== undefined && { defaultGhinIndexSetDaysPrior: league.defaultGhinIndexSetDaysPrior ?? org?.defaultGhinIndexSetDaysPrior }),
        ...((league.defaultSignupPriority ?? org?.defaultSignupPriority) !== undefined && { defaultSignupPriority: league.defaultSignupPriority ?? org?.defaultSignupPriority }),
        ...((league.defaultStartTime ?? org?.defaultStartTime) !== undefined && { defaultStartTime: league.defaultStartTime ?? org?.defaultStartTime }),
        ...((league.defaultMaxPlayers ?? org?.defaultMaxPlayers) !== undefined && { defaultMaxPlayers: league.defaultMaxPlayers ?? org?.defaultMaxPlayers }),
      };

      await setDoc(leagueRef, newLeague);
      logAnalyticsEvent('league_created', { org_id: league.orgId });
      return newLeague;
    } catch (error) {
      console.error('Error adding league:', error);
      return null;
    }
  };

  const archiveLeague = async (league: League): Promise<void> => {
    if (!league.itemId || !league.orgId) {
      console.error('League itemId or orgId not found:', league.itemId, league.orgId);
      return;
    }

    if (league?.isCatchAll && !isGod) {
      console.error('Cannot modify catch-all league. user: ', userState.userProfile?.itemId);
      return;
    }

    if (!isOrgAdmin(league.orgId)) {
      console.error('Permission denied. User:', userState.userProfile?.itemId, 'League:', league.itemId);
      return;
    }

    try {
      const leagueRef = doc(firestore, `org/${league.orgId}/league/${league.itemId}`);
      await updateDoc(leagueRef, { status: LEAGUE_STATUSES.ARCHIVED });
    } catch (error) {
      console.error('Error archiving league:', error);
    }
  };

  const deleteLeague = async (league: League, isPermanent = false): Promise<void> => {
    if (!league.itemId || !league.orgId) {
      console.error('League itemId or orgId not found:', league.itemId, league.orgId);
      return;
    }

    if (league?.isCatchAll && !isGod) {
      console.error('Cannot modify catch-all league. user: ', userState.userProfile?.itemId);
      return;
    }

    if (!isOrgAdmin(league.orgId)) {
      console.error('Permission denied. User:', userState.userProfile?.itemId, 'League:', league.itemId);
      return;
    }

    try {
      const leagueRef = doc(firestore, `org/${league.orgId}/league/${league.itemId}`);
      if (isPermanent) {
        await deleteDoc(leagueRef);
      } else {
        await updateDoc(leagueRef, { status: LEAGUE_STATUSES.DELETED });
      }
    } catch (error) {
      console.error('Error deleting league:', error);
    }
  };

  const restoreLeague = async (league: Partial<League>): Promise<void> => {
    if (!league.orgId || !league.itemId) {
      console.error('Missing required fields:', { orgId: league.orgId, itemId: league.itemId });
      return;
    }

    if (league?.isCatchAll && !isGod) {
      console.error('Cannot modify catch-all league. user: ', userState.userProfile?.itemId);
      return;
    }

    if (!isGod) {
      console.error('Permission denied: must be Gratisports employee.');
      return;
    }

    try {
      // extract the orgId and itemId from the league object as changing them is not allowed
      const { orgId, itemId } = league;
      const leagueRef = doc(firestore, `org/${orgId}/league/${itemId}`);
      await updateDoc(leagueRef, { status: LEAGUE_STATUSES.ACTIVE });
    } catch (error) {
      console.error('Error updating league:', error);
    }
  };

  const updateLeague = async (league: Partial<League>): Promise<void> => {
    if (!league.orgId || !league.itemId) {
      console.error('Missing required fields:', { orgId: league.orgId, itemId: league.itemId });
      return;
    }

    if (league?.isCatchAll && !isGod) {
      console.error('Cannot modify catch-all league. user: ', userState.userProfile?.itemId);
      return;
    }

    if (!isLeagueAdmin(league.itemId, league.orgId) && !isOrgAdmin(league.orgId)) {
      console.error('Permission denied: must be admin of organization');
      return;
    }

    try {
      // extract the orgId and itemId from the league object as changing them is not allowed
      const { orgId, itemId, ...leagueUpdates } = league;
      const leagueRef = doc(firestore, `org/${orgId}/league/${itemId}`);
      await setDoc(leagueRef, { ...leagueUpdates }, { merge: true });
    } catch (error) {
      console.error('Error updating league:', error);
    }
  };

  return {
    addLeague,
    archiveLeague,
    deleteLeague,
    restoreLeague,
    updateLeague,
  };
};
